import { useEffect, useMemo } from "react";
import {
    useCompanyStore,
    useDisclosureStore,
    useHelperStore,
    useTabStore,
    useCategoryStore
} from "~/stores";

import DisclosureItem from "./DisclosureItem";
import { NormalizedDisclosure, SEARCH_TABS } from "~/types/search.types";
import { SearchActions } from "~/types/actions.enum";

const DisclosureItemList = () => {
    // Store states
    const disclosures = useDisclosureStore(state => state.disclosures);
    const activeDisclosure = useDisclosureStore(state => state.disclosure);
    const isFetching = useHelperStore(state => state.isLoading);
    const activeTab = useTabStore(state => state.tab);
    const totalCompanies = useCompanyStore(state => state.totalSelected);
    const getCategoriesByProperty = useCategoryStore(state => state.getCategoriesByProperty);

    // Store dispatch methods
    const categoryDispatch = useCategoryStore(state => state.dispatch);
    const disclosureDispatch = useDisclosureStore(state => state.dispatch);

    const shouldDisableUpdate = useMemo(
        () => activeTab?.key === SEARCH_TABS.FULL_DOCUMENTS && totalCompanies > 0,
        [activeTab, totalCompanies]
    );

    const setFirstCategoryForDisclosure = (disclosureId: number) => {
        const categories = getCategoriesByProperty("disclosureId", disclosureId);
        categoryDispatch({
            type: SearchActions.SET_ACTIVE_CATEGORY,
            payload: categories?.[0] || null,
        });
    };

    const handleBtnClick = (disclosureItem: NormalizedDisclosure) => {
        if (shouldDisableUpdate) return;

        disclosureDispatch({
            type: SearchActions.SET_ACTIVE_DISCLOSURE,
            payload: disclosureItem,
        });

        setFirstCategoryForDisclosure(disclosureItem.exploreId);
    };

    useEffect(() => {
        if (activeDisclosure) {
            setFirstCategoryForDisclosure(activeDisclosure.exploreId);
        }
    }, [activeDisclosure, getCategoriesByProperty]);

    const disclosuresList = useMemo(() => {
        return disclosures.map(item => {
            const isActive = activeDisclosure?.id === item.id;

            const categories = getCategoriesByProperty("disclosureId", item.exploreId);
            const hasSelectedTopics = categories?.some(cat => cat.selectedTopicIds?.length > 0);
            const hasSelectedCategories = item?.selectedCategories?.length > 0;
            const hasSelectedCompanies =
                isActive &&
                activeTab?.key === SEARCH_TABS.FULL_DOCUMENTS &&
                totalCompanies > 0;

            const hasSelectedItems =
                hasSelectedCategories || hasSelectedTopics || hasSelectedCompanies;

            return (
                <DisclosureItem
                    key={item.id}
                    disclosure={item}
                    isActive={isActive}
                    isFetching={isFetching}
                    hasSelectedItems={hasSelectedItems}
                    btnClickCallback={handleBtnClick}
                />
            );
        });
    }, [
        disclosures,
        activeDisclosure,
        isFetching,
        totalCompanies,
        getCategoriesByProperty,
        activeTab,
    ]);

    return <div className="flex flex-col gap-1">{disclosuresList}</div>;
};

export default DisclosureItemList;
